import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Heart, Bell, Star, Truck, Check, ArrowLeft, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { useMarket } from "@/context/MarketContext";
import { formatPrice, FLAGS, stockLabel } from "@/lib/format";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";

export default function ProductDetail() {
  const { id } = useParams();
  const { t } = useI18n();
  const { user } = useAuth();
  const { favIds, refreshFavs } = useMarket();
  const [product, setProduct] = useState(null);
  const [target, setTarget] = useState("");
  const [alertOpen, setAlertOpen] = useState(false);

  const isFav = favIds.includes(id);

  useEffect(() => {
    api.get(`/products/${id}`).then(({ data }) => setProduct(data)).catch(() => setProduct(false));
  }, [id]);

  const toggleFav = async () => {
    if (!user) { toast.error(t("common.loginPrompt")); return; }
    if (isFav) await api.delete(`/favorites/${id}`); else await api.post(`/favorites/${id}`);
    refreshFavs();
    toast.success(isFav ? t("common.remove") : t("product.addfav"));
  };

  const createAlert = async () => {
    if (!user) { toast.error(t("common.loginPrompt")); return; }
    if (!target) return;
    await api.post("/alerts", { product_id: id, target_price: parseFloat(target) });
    setAlertOpen(false);
    setTarget("");
    toast.success(t("product.setalert"));
  };

  if (product === null) return <div className="py-20 text-center text-slate-400">{t("common.loading")}</div>;
  if (product === false) return <div className="py-20 text-center text-slate-500">Not found</div>;

  const best = product.offers[0];

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      <Link to="/search" className="mb-6 inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-primary">
        <ArrowLeft className="h-4 w-4" /> {t("nav.categories")}
      </Link>

      <div className="grid gap-8 lg:grid-cols-[1fr_340px]">
        <div>
          {/* Header */}
          <div className="rounded-lg border border-border bg-white p-6">
            <span className="text-xs font-bold uppercase tracking-[0.18em] text-primary">{product.brand}</span>
            <h1 className="mt-2 font-heading text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">{product.name}</h1>
            <p className="mt-2 text-slate-600">{product.description}</p>
            <div className="mt-4 flex flex-wrap gap-2">
              <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">{t("product.ean")}: {product.ean}</span>
              {product.oem_numbers.map((o) => (
                <span key={o} className="rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-primary">{o}</span>
              ))}
            </div>
          </div>

          {/* Offers comparison table */}
          <div className="mt-6">
            <h2 className="font-heading text-xl font-semibold tracking-tight text-slate-900">{t("product.offers")}</h2>
            <div className="mt-4 overflow-x-auto rounded-lg border border-border bg-white">
              <table className="w-full border-collapse text-sm" data-testid="offers-table">
                <thead>
                  <tr className="border-b border-border bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                    <th className="px-4 py-3 font-semibold">{t("product.seller")}</th>
                    <th className="px-4 py-3 font-semibold">{t("product.price")}</th>
                    <th className="px-4 py-3 font-semibold">{t("product.shipping")}</th>
                    <th className="px-4 py-3 font-semibold">{t("product.delivery")}</th>
                    <th className="px-4 py-3 font-semibold">{t("product.stock")}</th>
                    <th className="px-4 py-3 font-semibold">{t("product.total")}</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {product.offers.map((o, i) => {
                    const st = stockLabel(o.stock_status, t);
                    return (
                      <tr key={o.id} data-testid={`offer-row-${i}`} className={`border-b border-border last:border-0 transition-colors duration-200 hover:bg-slate-50 ${i === 0 ? "bg-blue-50/40" : ""}`}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2 font-medium text-slate-900">
                            {o.retailer?.name}
                            {o.retailer?.verified && <Check className="h-3.5 w-3.5 text-emerald-500" />}
                          </div>
                          <div className="mt-0.5 flex items-center gap-1 text-xs text-slate-400">
                            <Star className="h-3 w-3 fill-amber-400 text-amber-400" /> {o.retailer?.rating} · {FLAGS[o.country]} {o.country}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-slate-700">{formatPrice(o.price)}</td>
                        <td className="px-4 py-3 text-slate-700">{o.shipping_cost === 0 ? <span className="text-emerald-600">{t("product.free")}</span> : formatPrice(o.shipping_cost)}</td>
                        <td className="px-4 py-3 text-slate-700"><span className="inline-flex items-center gap-1"><Truck className="h-3.5 w-3.5 text-slate-400" />{o.delivery_days} {t("product.days")}</span></td>
                        <td className="px-4 py-3"><span className={`rounded-full border px-2 py-0.5 text-xs font-medium ${st.cls}`}>{st.label}</span></td>
                        <td className="px-4 py-3">
                          <div className="font-heading text-base font-bold text-slate-900">{formatPrice(o.total_price)}</div>
                          {i === 0 && <span className="text-[10px] font-bold uppercase tracking-wide text-primary">{t("product.bestdeal")}</span>}
                        </td>
                        <td className="px-4 py-3">
                          <a href={o.url} data-testid={`goto-seller-${i}`} className="inline-flex items-center gap-1 rounded-full bg-primary px-3 py-1.5 text-xs font-semibold text-white transition-colors duration-200 hover:bg-blue-600">
                            {t("product.gotoseller")} <ExternalLink className="h-3 w-3" />
                          </a>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Compatibility */}
          <div className="mt-6 rounded-lg border border-border bg-white p-6">
            <h2 className="font-heading text-xl font-semibold tracking-tight text-slate-900">{t("product.compat")}</h2>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              {product.compatibility.map((c, i) => (
                <div key={i} className="rounded-lg border border-border bg-slate-50 p-4">
                  <div className="font-heading font-semibold tracking-tight text-slate-900">{c.make} {c.model}</div>
                  <div className="mt-1 text-sm text-slate-500">{c.years} · {c.engine}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <aside className="h-fit lg:sticky lg:top-24">
          <div className="rounded-lg border border-border bg-white p-6">
            <div className="text-xs text-slate-400">{t("search.from")}</div>
            <div className="font-heading text-4xl font-bold tracking-tight text-slate-900">{formatPrice(product.summary.min_total)}</div>
            <div className="mt-1 text-sm text-slate-500">{product.summary.offer_count} {t("search.offers")} · {product.summary.best_delivery} {t("product.days")}</div>

            <a href={best?.url} data-testid="buy-best-btn" className="mt-5 flex w-full items-center justify-center gap-2 rounded-full bg-primary px-6 py-3 font-heading font-semibold text-white transition-colors duration-200 hover:bg-blue-600">
              {t("product.gotoseller")} <ExternalLink className="h-4 w-4" />
            </a>

            <div className="mt-3 grid grid-cols-2 gap-2">
              <button onClick={toggleFav} data-testid="detail-fav-btn"
                className={`flex items-center justify-center gap-1.5 rounded-full border px-4 py-2.5 text-sm font-semibold transition-colors duration-200 ${isFav ? "border-rose-200 bg-rose-50 text-rose-600" : "border-border text-slate-700 hover:border-rose-300 hover:text-rose-600"}`}>
                <Heart className="h-4 w-4" fill={isFav ? "currentColor" : "none"} /> {isFav ? t("product.remfav") : t("product.addfav")}
              </button>

              <Dialog open={alertOpen} onOpenChange={setAlertOpen}>
                <DialogTrigger asChild>
                  <button data-testid="alert-open-btn" className="flex items-center justify-center gap-1.5 rounded-full border border-border px-4 py-2.5 text-sm font-semibold text-slate-700 transition-colors duration-200 hover:border-primary hover:text-primary">
                    <Bell className="h-4 w-4" /> {t("nav.alerts")}
                  </button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>{t("product.setalert")}</DialogTitle></DialogHeader>
                  <p className="text-sm text-slate-500">{product.name}</p>
                  <input data-testid="alert-target-input" type="number" value={target} onChange={(e) => setTarget(e.target.value)}
                    placeholder={t("alerts.target")} className="w-full rounded-lg border border-border px-4 py-2.5 text-sm outline-none focus-visible:ring-2 focus-visible:ring-primary" />
                  <DialogFooter>
                    <button onClick={createAlert} data-testid="alert-save-btn" className="rounded-full bg-primary px-6 py-2.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-blue-600">{t("common.save")}</button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
